import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { comics } from "@/mocks/comics";
import ComicCard from "@/components/feature/ComicCard";

export default function ComicDetail() {
  const { id } = useParams();
  const comic = comics.find((c) => c.id === Number(id));

  const chapters = useMemo(() => {
    if (!comic) return [];
    const count = Math.min(comic.episodes, 12);
    return Array.from({ length: count }, (_, i) => ({
      no: comic.episodes - i,
      title: `第 ${comic.episodes - i} 话`,
      lock: i < 3 ? "free" : "later",
    }));
  }, [comic]);

  if (!comic) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
        <h1 className="mt-4 font-display text-2xl text-foreground-950">未找到这部韩漫</h1>
        <p className="mt-2 text-sm text-foreground-500">该作品可能已下线或链接有误</p>
        <Link
          to="/ranking"
          className="mt-6 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回排行榜
        </Link>
      </div>
    );
  }

  const related = comics.filter((c) => c.id !== comic.id && c.genres.some((g) => comic.genres.includes(g))).slice(0, 4);

  return (
    <>
      {/* Detail hero */}
      <section className="border-b border-background-200/70 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-12">
          <nav aria-label="面包屑" className="mb-6 flex flex-wrap items-center gap-1 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-500">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/ranking" className="transition-colors hover:text-primary-500">漫画榜单</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">{comic.title}</span>
          </nav>

          <div className="flex flex-col gap-7 md:flex-row">
            <div className="shrink-0">
              <div className="w-full overflow-hidden rounded-2xl border border-background-200/70 md:w-60">
                <img
                  src={comic.cover}
                  alt={`${comic.title} 免费韩国漫画封面`}
                  title={`${comic.title} 韩漫推荐`}
                  className="aspect-[5/7] w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                {comic.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/ranking?genre=${encodeURIComponent(g)}`}
                    className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-500 hover:text-background-50"
                  >
                    {g}
                  </Link>
                ))}
                <span className={`ml-auto whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold ${
                  comic.status === "完结" ? "bg-accent-500 text-background-50" : "bg-primary-500 text-background-50"
                }`}>
                  {comic.status}
                </span>
              </div>

              <h1 className="mt-3 font-display text-3xl text-foreground-950 md:text-4xl">{comic.title}</h1>
              <p className="mt-2 text-sm text-foreground-600">
                作者：<span className="font-medium text-foreground-800">{comic.author}</span> · 更新：{comic.update}
              </p>

              <div className="mt-5 grid max-w-lg grid-cols-3 gap-4">
                {[
                  { label: "评分", value: comic.rating.toFixed(1), icon: "ri-star-fill", cls: "text-secondary-500" },
                  { label: "话数", value: `${comic.episodes}话`, icon: "ri-book-3-line", cls: "text-primary-500" },
                  { label: "人气", value: comic.views, icon: "ri-eye-line", cls: "text-accent-600" },
                ].map((s) => (
                  <div key={s.label} className="rounded-xl border border-background-200/70 bg-background-50 p-3.5 text-center">
                    <i className={`${s.icon} ${s.cls} text-lg`}></i>
                    <p className="mt-1.5 font-display text-lg text-foreground-950">{s.value}</p>
                    <p className="text-xs text-foreground-500">{s.label}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-2">
                {comic.tags.map((t) => (
                  <span key={t} className="whitespace-nowrap rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-600">
                    # {t}
                  </span>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button className="whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
                  <i className="ri-play-fill mr-1"></i>开始阅读
                </button>
                <button className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-7 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-500">
                  <i className="ri-heart-line mr-1"></i>收藏
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-12">
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="flex items-center gap-2 font-display text-xl text-foreground-950 md:text-2xl">
              <i className="ri-file-text-line text-primary-500"></i>
              作品简介
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700">{comic.description}</p>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              作为{comic.genres.join("、")}题材的优质韩漫，《{comic.title}》以其细腻的画风和扎实的剧情收获了众多读者喜爱。
              从人物刻画到情感铺垫都颇具功力，是{comic.status === "完结" ? "可以放心一次追完" : "值得长期追更"}的人气作品。
            </p>

            <h2 className="mt-9 flex items-center gap-2 font-display text-xl text-foreground-950 md:text-2xl">
              <i className="ri-list-check-3 text-primary-500"></i>
              章节列表
            </h2>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {chapters.map((ch) => (
                <li key={ch.no}>
                  <button
                    className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-sm transition-colors ${
                      ch.lock === "free"
                        ? "border-primary-200 bg-primary-50/60 text-foreground-800 hover:border-primary-400"
                        : "border-background-200/70 bg-background-100 text-foreground-500 hover:border-background-300"
                    }`}
                  >
                    <span className="truncate">{ch.title}</span>
                    {ch.lock === "free" ? (
                      <span className="ml-2 shrink-0 text-xs font-medium text-primary-500">免费阅读</span>
                    ) : (
                      <i className="ri-lock-line shrink-0 text-xs"></i>
                    )}
                  </button>
                </li>
              ))}
            </ul>
            {comic.episodes > 12 && (
              <p className="mt-4 text-center text-xs text-foreground-500">
                已展示最近 12 话，共 {comic.episodes} 话 · 更多章节请开始阅读查看
              </p>
            )}
          </div>

          {/* Sidebar */}
          <aside>
            <h2 className="flex items-center gap-2 font-display text-xl text-foreground-950">
              <i className="ri-thumb-up-line text-primary-500"></i>
              相关推荐
            </h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
              {related.length > 0 ? (
                related.map((c) => <ComicCard key={c.id} comic={c} />)
              ) : (
                <p className="rounded-xl border border-dashed border-background-300 bg-background-100 p-6 text-center text-sm text-foreground-500">
                  暂无更多相关推荐
                </p>
              )}
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}